import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MapPin, Navigation, Phone, Package, Clock, Euro,
  CheckCircle2, Truck, RefreshCw, Route, ExternalLink,
  User, PlayCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function CityMapView({ sessionToken, businessAddress }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);

  // Fetch deliveries
  const fetchDeliveries = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/stations/driver/deliveries`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries || []);
      }
    } catch (error) {
      console.error('Fetch deliveries error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchDeliveries();
    const interval = setInterval(fetchDeliveries, 15000);
    return () => clearInterval(interval);
  }, [fetchDeliveries]);

  // Generate Google Maps embed URL with all delivery addresses
  const getMapEmbedUrl = () => {
    const addresses = deliveries
      .filter(d => d.address)
      .map(d => encodeURIComponent(d.address))
      .slice(0, 10); // Max 10 for embed
    
    if (addresses.length === 0) {
      // Default to Germany if no addresses
      return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Germany&zoom=6`;
    }
    
    // Use directions mode with multiple waypoints
    const origin = businessAddress || addresses[0];
    const destination = addresses[addresses.length - 1];
    const waypoints = addresses.slice(0, -1).join('|');
    
    return `https://www.google.com/maps/embed/v1/directions?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&origin=${encodeURIComponent(origin)}&destination=${destination}&waypoints=${waypoints}&mode=driving`;
  };

  // Open Google Maps navigation
  const openNavigation = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  // Open route with multiple stops
  const openRoute = () => {
    if (selectedDeliveries.length === 0) {
      toast.error('Wählen Sie mindestens eine Lieferung aus');
      return;
    }
    
    const addresses = selectedDeliveries
      .map(id => deliveries.find(d => d.order_id === id)?.address)
      .filter(Boolean);
    
    if (addresses.length === 0) return;
    
    const destination = encodeURIComponent(addresses[addresses.length - 1]);
    const waypoints = addresses.slice(0, -1).map(a => encodeURIComponent(a)).join('|');
    
    let url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    if (waypoints) url += `&waypoints=${waypoints}`;
    
    window.open(url, '_blank');
  };

  // Toggle selection
  const toggleSelection = (orderId) => {
    setSelectedDeliveries(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Start delivery
  const startDelivery = async (orderId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/driver/start-delivery?order_id=${orderId}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        toast.success('Lieferung gestartet!');
        fetchDeliveries();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Complete delivery
  const completeDelivery = async (orderId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/driver/complete-delivery?order_id=${orderId}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        toast.success('Lieferung abgeschlossen!');
        fetchDeliveries();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const readyDeliveries = deliveries.filter(d => d.status === 'ready');
  const activeDeliveries = deliveries.filter(d => d.status === 'on_the_way');

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Stadtplan</h1>
          <p className="text-gray-400">
            {deliveries.length} Lieferungen • {readyDeliveries.length} bereit • {activeDeliveries.length} unterwegs
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedDeliveries.length > 0 && (
            <Button onClick={openRoute} className="bg-blue-600 hover:bg-blue-700">
              <Route className="w-4 h-4 mr-2" />
              Route planen ({selectedDeliveries.length})
            </Button>
          )}
          <Button onClick={fetchDeliveries} variant="outline" className="border-gray-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card className="bg-[#12121a] border-gray-800 h-full">
            <CardContent className="p-0 h-full">
              {deliveries.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MapPin className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Keine aktiven Lieferungen</p>
                    <p className="text-sm mt-1">Lieferungen erscheinen hier auf der Karte</p>
                  </div>
                </div>
              ) : (
                <div className="relative h-full min-h-[500px]">
                  {/* Google Maps Embed - Placeholder */}
                  <div className="absolute inset-0 bg-gray-800 rounded-lg overflow-hidden">
                    <div className="h-full w-full flex items-center justify-center">
                      <div className="text-center">
                        <MapPin className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                        <p className="text-white text-lg font-medium mb-2">
                          {deliveries.length} Lieferadressen
                        </p>
                        <p className="text-gray-400 text-sm mb-4">
                          Klicken Sie auf "Route planen" oder eine einzelne Adresse
                        </p>
                        <Button
                          onClick={() => {
                            if (deliveries[0]?.address) {
                              openNavigation(deliveries[0].address);
                            }
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          In Google Maps öffnen
                        </Button>
                      </div>
                    </div>
                    
                    {/* Address pins visualization */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex flex-wrap gap-2">
                        {deliveries.slice(0, 6).map((d, idx) => (
                          <Badge 
                            key={d.order_id}
                            className="bg-purple-500/80 text-white cursor-pointer hover:bg-purple-600"
                            onClick={() => openNavigation(d.address)}
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            #{d.order_number}
                          </Badge>
                        ))}
                        {deliveries.length > 6 && (
                          <Badge className="bg-gray-600 text-white">
                            +{deliveries.length - 6} weitere
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Deliveries List */}
        <div>
          <Card className="bg-[#12121a] border-gray-800 h-full">
            <CardHeader className="border-b border-gray-800 pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-400" />
                Lieferungen
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-300px)]">
                {loading ? (
                  <div className="p-8 text-center text-gray-400">Lade...</div>
                ) : deliveries.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    Keine Lieferungen
                  </div>
                ) : (
                  <div className="divide-y divide-gray-800">
                    {/* Ready Deliveries */}
                    {readyDeliveries.length > 0 && (
                      <div className="p-3 bg-green-500/10">
                        <div className="text-sm font-medium text-green-400 mb-2">
                          Bereit zur Abholung ({readyDeliveries.length})
                        </div>
                        {readyDeliveries.map((delivery) => (
                          <div
                            key={delivery.order_id}
                            onClick={() => toggleSelection(delivery.order_id)}
                            className={`
                              p-3 rounded-lg mb-2 cursor-pointer transition-colors
                              ${selectedDeliveries.includes(delivery.order_id)
                                ? 'bg-green-500/20 border border-green-500/50'
                                : 'bg-gray-800/50 hover:bg-gray-800'}
                            `}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-white">#{delivery.order_number}</span>
                              <Badge className="bg-green-500/20 text-green-400 border-0">
                                Bereit
                              </Badge>
                            </div>
                            
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center gap-2 text-gray-300">
                                <User className="w-3 h-3" />
                                {delivery.customer_name || 'Unbekannt'}
                              </div>
                              <div className="flex items-start gap-2 text-gray-400">
                                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">{delivery.address}</span>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-amber-400 font-bold">
                                  {delivery.total?.toFixed(2)} €
                                </span>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openNavigation(delivery.address);
                                    }}
                                    className="h-7 px-2 text-blue-400"
                                  >
                                    <Navigation className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startDelivery(delivery.order_id);
                                    }}
                                    className="h-7 px-2 bg-green-600 hover:bg-green-700"
                                  >
                                    <PlayCircle className="w-3 h-3 mr-1" />
                                    Los
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Active Deliveries */}
                    {activeDeliveries.length > 0 && (
                      <div className="p-3 bg-purple-500/10">
                        <div className="text-sm font-medium text-purple-400 mb-2">
                          Unterwegs ({activeDeliveries.length})
                        </div>
                        {activeDeliveries.map((delivery) => (
                          <div
                            key={delivery.order_id}
                            className="p-3 rounded-lg mb-2 bg-gray-800/50"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-white">#{delivery.order_number}</span>
                              <Badge className="bg-purple-500/20 text-purple-400 border-0">
                                Unterwegs
                              </Badge>
                            </div>
                            
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center gap-2 text-gray-300">
                                <User className="w-3 h-3" />
                                {delivery.customer_name || 'Unbekannt'}
                              </div>
                              <div className="flex items-start gap-2 text-gray-400">
                                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">{delivery.address}</span>
                              </div>
                              {delivery.customer_phone && (
                                <a 
                                  href={`tel:${delivery.customer_phone}`}
                                  className="flex items-center gap-2 text-blue-400"
                                >
                                  <Phone className="w-3 h-3" />
                                  {delivery.customer_phone}
                                </a>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-amber-400 font-bold">
                                  {delivery.total?.toFixed(2)} €
                                  {!delivery.paid && (
                                    <span className="text-gray-500 text-xs ml-1">(kassieren)</span>
                                  )}
                                </span>
                                <Button
                                  size="sm"
                                  onClick={() => completeDelivery(delivery.order_id)}
                                  className="h-7 px-2 bg-purple-600 hover:bg-purple-700"
                                >
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Fertig
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
